import React, {Fragment, useEffect, useState, useContext} from 'react';
import {Tabs, Breadcrumb, Tooltip, Row, Col, Badge, notification, Typography} from 'antd';
import {useRouteMatch, Link} from 'react-router-dom';
import {extract} from '../../utils/openapi-extractor';
import {EyeOutlined, CodeOutlined, MacCommandOutlined} from '@ant-design/icons';
import {SchemaBlock} from './schema-block';
import {ModelIcon} from '../type-icons';
import {TechnicalEditor} from '../technical-editor';
import {Block} from '../block';
import {OpenApiContext} from '../../openapi-context';
import {AppContext} from '../../state';
import {LintView} from '../lint-view';
import {errorsForModel} from '../../utils/openapi-schema-validator';
import {useHistory} from 'react-router-dom';
import {useLocation} from 'react-use';

const OPENAPI_IDENTIFIER_REGEXP = /^[a-zA-Z0-9.\-_]+$/;

// Inline-editable model name. Click the text → input takes over the same
// slot (Excel cell pattern). Enter or blur commits, Escape cancels.
//
// Two subtleties baked in here that the old modal-based dialog had wrong:
//
//   1. URL segment is the verbatim schema key, NOT snake_case. The
//      schemas extractor (utils/openapi-extractor.js:34-46) uses the
//      JSON key as both name and key; URL segments match those
//      verbatim. snakeCase()'ing the new name produced a URL that no
//      route matched.
//
//   2. The dispatch (rename-model) and the URL update MUST happen in
//      the same synchronous tick so React batches them into one render.
//      Otherwise: dispatch lands first, spec moves the schema entry,
//      params.modelKey still points at the old name, ModelView extracts
//      undefined and renders null — a blank-screen frame until the URL
//      catches up.
function EditableModelTitle({model, canEdit, onRename, onEditingChange}) {
    const history = useHistory();
    const location = useLocation();

    const commitRename = (newName) => {
        // Always notify the parent that we've left edit mode, regardless
        // of whether the value is committed or rejected — keeps the
        // schema-block edit pencil ungrayed once the input closes.
        onEditingChange && onEditingChange(false);

        const trimmed = (newName || '').trim();
        if (!trimmed || trimmed === model.name) return;
        if (!OPENAPI_IDENTIFIER_REGEXP.test(trimmed)) {
            notification.error({
                message: 'Invalid Name',
                description: 'Model name must be a valid OpenAPI identifier (letters, digits, .-_).',
                placement: 'bottomRight',
            });
            return;
        }
        try {
            onRename(trimmed);
            const fragments = location.pathname.split('/');
            fragments.splice(-1, 1, trimmed);
            history.replace({pathname: fragments.join('/')});
            // No success notification here — library.js's auto-save
            // useEffect already fires "Changes successfully saved!" for
            // every spec mutation, so a second toast just stacks noise.
        } catch (err) {
            notification.error({message: 'Rename Error', description: err.message, placement: 'bottomRight'});
        }
    };

    const editableProp = canEdit
        ? {
              tooltip: 'Click to rename',
              triggerType: ['text'],
              onStart: () => onEditingChange && onEditingChange(true),
              onChange: commitRename,
              onCancel: () => onEditingChange && onEditingChange(false),
              enterIcon: null,
          }
        : false;

    return (
        <Typography.Title level={2} className="modelTitle__name" editable={editableProp}>
            {model.spec.title || model.name}
        </Typography.Title>
    );
}

export const ModelView = function ({spec, dispatch, rootPath, mobile, currentHash}) {
    const app = useContext(AppContext);
    const {state, SUPPORTED_EDITOR_ROLES} = useContext(OpenApiContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version/models/:modelKey');
    const [model, updateModel] = useState();
    const [errorCount, setErrorCount] = useState(0);
    // Tracks whether the inline title-rename input is open. Used to
    // gray out the schema-block's edit pencil while the user is mid-
    // rename — the two edit modes (title input + block-level edit)
    // would otherwise interleave and abort each other on click.
    const [isTitleEditing, setIsTitleEditing] = useState(false);

    useEffect(() => {
        updateModel(extract(spec).model(params.modelKey));
        app.dispatch({
            type: 'add-breadcrumb-entry',
            entry: {
                env: params.env,
                linkType: 'models',
                category: params.category,
                key: params.key,
                version: params.version,
                modelKey: params.modelKey,
            },
        });
    }, [spec, params.modelKey]);

    useEffect(() => {
        if (model) {
            setErrorCount(errorsForModel(state.specValidationErrors, model.name).length);
        } else {
            setErrorCount(0);
        }
    }, [spec, params.modelKey, model, state.specValidationErrors]);

    function onTechnicalEdit(newModel) {
        dispatch({
            type: 'update-model',
            model: {name: model.name, key: model.key, spec: newModel ? newModel : {title: model.name, type: 'object', properties: {}}},
        });
    }

    function onSchemaSave(model) {
        return function (spec) {
            model.spec = spec;
            dispatch({type: 'update-model', model});
        };
    }

    function onModelRename(newName) {
        dispatch({type: 'rename-model', previous: model.name, name: newName});
    }

    const canEdit = () => SUPPORTED_EDITOR_ROLES.indexOf(state.role) !== -1 && !mobile;

    return model ? (
        <Fragment>
            <Row>
                <Col xs={24}>
                    <div className="breadcrumbContainer">
                        <Breadcrumb separator=">">
                            {app.state.breadcrumb.length > 1 &&
                                app.state.breadcrumb.map((entry) => (
                                    <Breadcrumb.Item key={entry.url}>
                                        <Tooltip title={`Jump to ${entry.category}/${entry.key}`}>
                                            <Link to={entry.url}>
                                                {entry.title}[{entry.version}]
                                            </Link>
                                        </Tooltip>
                                    </Breadcrumb.Item>
                                ))}
                        </Breadcrumb>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <Tabs
                        type="card"
                        tabPlacement="right"
                        id="modelTabs"
                        items={[
                            {
                                key: 'visual',
                                label: <EyeOutlined />,
                                children: (
                                    <div className="scrollableViewer">
                                        <SchemaBlock
                                            schema={model.spec}
                                            onSave={onSchemaSave(model)}
                                            rootPath={rootPath}
                                            mobile={mobile}
                                            disableEdit={isTitleEditing}
                                            header={
                                                <div className="modelTitle">
                                                    <ModelIcon type={model.spec.type || 'object'} width="2em" />
                                                    <EditableModelTitle
                                                        model={model}
                                                        canEdit={canEdit() && !state.editMode}
                                                        onRename={onModelRename}
                                                        onEditingChange={setIsTitleEditing}
                                                    />
                                                </div>
                                            }
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'technical',
                                label: <CodeOutlined />,
                                disabled: state.editMode,
                                children: (
                                    <>
                                        <h2>
                                            <ModelIcon type={model.spec.type || 'object'} width="2em" /> <span style={{marginLeft: 10}}>{model.name}</span>
                                        </h2>
                                        <Block noEdit>
                                            <TechnicalEditor object={model.spec} update={onTechnicalEdit} mobile={mobile} />
                                        </Block>
                                    </>
                                ),
                            },
                            {
                                key: 'lint',
                                label: errorCount > 0 ? (
                                    <Badge count={errorCount}>
                                        <MacCommandOutlined />
                                    </Badge>
                                ) : (
                                    <MacCommandOutlined />
                                ),
                                disabled: state.editMode,
                                children: (
                                    <Block noEdit>
                                        <LintView type="models" modelKey={model.name} currentHash={currentHash} />
                                    </Block>
                                ),
                            },
                        ]}
                    />
                </Col>
            </Row>
        </Fragment>
    ) : null;
};
